import React, { useCallback, useEffect, useState } from 'react';
import {
  adFreeEntitlement,
  createAdFreeCheckoutSession,
  fetchEntitlements,
  isAdFreeActive,
  type PaymentEntitlement,
} from '../../services/paymentEntitlementService';

interface AdFreePurchaseCardProps {
  language: string;
  isRTL?: boolean;
}

function statusLabel(entitlement: PaymentEntitlement | undefined, ar: boolean): string {
  if (!entitlement) {
    return ar ? 'الإعلانات مفعّلة (لا يوجد Ad-Free نشط)' : 'Ads are on (no active Ad-Free)';
  }
  if (entitlement.active && entitlement.status === 'cancelled_at_period_end') {
    return ar
      ? 'Ad-Free نشط حتى نهاية الفترة (إلغاء مجدول)'
      : 'Ad-Free active until period end (cancel scheduled)';
  }
  if (entitlement.active) {
    return ar ? 'Ad-Free نشط — الإعلانات المؤهلة متوقفة' : 'Ad-Free active — eligible ads suppressed';
  }
  if (entitlement.status === 'expired') {
    return ar ? 'انتهى Ad-Free — عادت الإعلانات' : 'Ad-Free expired — ads restored';
  }
  if (entitlement.status === 'refunded' || entitlement.status === 'revoked') {
    return ar ? 'توقف Ad-Free — عادت الإعلانات' : 'Ad-Free ended — ads restored';
  }
  return ar ? 'الإعلانات مفعّلة' : 'Ads are on';
}

export const AdFreePurchaseCard: React.FC<AdFreePurchaseCardProps> = ({
  language,
}) => {
  const ar = language === 'ar';
  const [entitlement, setEntitlement] = useState<PaymentEntitlement | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchEntitlements();
      setEntitlement(adFreeEntitlement(list));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onSubscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      const session = await createAdFreeCheckoutSession();
      window.location.href = session.sessionUrl;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : ar
            ? 'تعذر بدء الدفع'
            : 'Could not start checkout',
      );
    } finally {
      setBusy(false);
    }
  };

  const active = entitlement ? isAdFreeActive([entitlement]) : false;

  return (
    <div className="bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-lg p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {ar ? 'بدون إعلانات (Ad-Free)' : 'Ad-Free'}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        {ar
          ? '50 جنيه شهرياً — يزيل الإعلانات المؤهلة. لا يشمل إصدارات المكتبة القانونية ولا رصيد الذكاء الاصطناعي.'
          : '50 EGP / month — removes eligible ads. Does not include legal library editions or AI tokens.'}
      </p>
      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">
        {loading ? (ar ? 'جاري التحميل…' : 'Loading…') : statusLabel(entitlement, ar)}
      </p>
      <p className="text-xs text-gray-500 mb-4">
        {ar
          ? 'الإلغاء يتم عبر مزود الدفع؛ تُحدَّث الحالة بعد مزامنة الـ webhook.'
          : 'Cancel is handled by the payment provider; status updates after webhook sync.'}
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <div className="flex flex-wrap gap-3">
        {!active && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onSubscribe()}
            className="px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
          >
            {busy
              ? ar
                ? 'جاري الفتح…'
                : 'Opening…'
              : ar
                ? 'اشترك في Ad-Free'
                : 'Subscribe to Ad-Free'}
          </button>
        )}
        <button
          type="button"
          disabled={loading}
          onClick={() => void refresh()}
          className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {ar ? 'تحديث الحالة' : 'Refresh status'}
        </button>
      </div>
    </div>
  );
};

export default AdFreePurchaseCard;
